import { UserRole } from "@features/settings/types/settings.types";
import { useProfile } from "@shared/hooks/useProfile";
import { UserProfile } from "@shared/types/user";
import theme from "@shared/utils/themeColors";
import { doc, getDoc } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { db } from "../../../config/firebase";
import { SUPPORT_TICKETS_COLLECTION } from "../constants/collections";
import { useSupport } from "../context/SupportContext";
import { useSupportMessages } from "../hooks/useSupportMessages";
import SupportMessageInput from "./SupportMessageInput";
import SupportMessageList from "./SupportMessageList";

interface SupportChatPanelProps {
  ticketId: string;
}

/**
 * Main chat panel for the admin support dashboard
 */
const SupportChatPanel: React.FC<SupportChatPanelProps> = ({ ticketId }) => {
  const { user } = useProfile();
  const { closeTicket, reopenTicket } = useSupport();
  const [ticketUser, setTicketUser] = useState<UserProfile | null>(null);
  const [ticketStatus, setTicketStatus] = useState<"open" | "closed">("open");
  const [isLoadingUser, setIsLoadingUser] = useState(true);

  const {
    messages,
    loading,
    hasMore,
    loadMoreMessages,
    updateTypingStatus,
    sendMessage,
    deleteMessage,
    typingUsers,
  } = useSupportMessages(ticketId);

  // Get ticket details and user info
  useEffect(() => {
    const fetchTicketAndUser = async () => {
      try {
        setIsLoadingUser(true);
        const ticketRef = doc(db, SUPPORT_TICKETS_COLLECTION, ticketId);
        const ticketSnap = await getDoc(ticketRef);

        if (ticketSnap.exists()) {
          const ticketData = ticketSnap.data();
          setTicketStatus(ticketData.status);

          // Get user info
          const userRef = doc(db, "users", ticketData.userId);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            setTicketUser(userSnap.data().profile);
          }
        }
      } catch (error) {
        console.error("Error fetching ticket details:", error);
      } finally {
        setIsLoadingUser(false);
      }
    };

    fetchTicketAndUser();
  }, [ticketId]);

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      updateTypingStatus(false);
    };
  }, [updateTypingStatus]);

  // Handle closing or reopening the ticket
  const handleToggleTicketStatus = async () => {
    try {
      if (ticketStatus === "open") {
        await closeTicket(ticketId);
        setTicketStatus("closed");
      } else {
        await reopenTicket(ticketId);
        setTicketStatus("open");
      }
    } catch (error) {
      console.error("Error updating ticket status:", error);
    }
  };

  // If user is not an admin, don't render this component
  if (user?.role !== UserRole.ADMIN) {
    return null;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Chat header */}
      <div
        className={`flex items-center justify-between p-4 border-b ${theme.border.primary}`}
      >
        <div className="flex items-center">
          {isLoadingUser ? (
            <div
              className={`animate-pulse w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 mr-3`}
            ></div>
          ) : (
            <div className="flex-shrink-0 mr-3">
              <img
                src={
                  ticketUser?.imageUrl ||
                  `https://ui-avatars.com/api/?name=${
                    ticketUser?.name || "User"
                  }`
                }
                alt={ticketUser?.name || "User"}
                className="w-10 h-10 rounded-full"
              />
            </div>
          )}

          <div>
            {isLoadingUser ? (
              <div className="space-y-2">
                <div
                  className={`animate-pulse w-24 h-4 rounded bg-gray-200 dark:bg-gray-700`}
                ></div>
                <div
                  className={`animate-pulse w-32 h-3 rounded bg-gray-200 dark:bg-gray-700`}
                ></div>
              </div>
            ) : (
              <>
                <h3 className={`font-semibold ${theme.text.primary}`}>
                  {ticketUser?.name || "User"}
                </h3>
                <p className={`text-xs ${theme.text.muted}`}>
                  {typingUsers &&
                  Object.values(typingUsers).some((isTyping) => isTyping)
                    ? "Typing..."
                    : ticketStatus === "open"
                    ? "Active conversation"
                    : "Conversation closed"}
                </p>
              </>
            )}
          </div>
        </div>

        {/* <div className="flex items-center space-x-2">
          <Button
            size={ButtonSize.SMALL}
            variant={
              ticketStatus === "open"
                ? ButtonVariant.DANGER
                : ButtonVariant.PRIMARY
            }
            onClick={handleToggleTicketStatus}
          >
            {ticketStatus === "open" ? "Close Ticket" : "Reopen Ticket"}
          </Button>
        </div> */}
      </div>

      {/* Chat messages */}
      <div className="flex-grow flex flex-col overflow-hidden h-1">
        <SupportMessageList
          messages={messages}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={loadMoreMessages}
          onDeleteMessage={deleteMessage}
          typingUsers={typingUsers}
        />
      </div>

      {/* Chat input */}
      <SupportMessageInput
        onSendMessage={sendMessage}
        updateTypingStatus={updateTypingStatus}
        disabled={ticketStatus === "closed"}
      />
    </div>
  );
};

export default SupportChatPanel;
